import React, { useState, useEffect } from 'react';
import { GameListItem } from '@/types/game';
import { api } from '@/utils/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from './LanguageSelector';

interface LobbyProps {
    sessionId: string;
    playerName: string;
    onNavigateToGame: (gameId: string) => void;
    showModal: (message: string, showCopy?: boolean, textToCopy?: string) => void;
}

export const Lobby: React.FC<LobbyProps> = ({
    sessionId,
    playerName,
    onNavigateToGame,
    showModal
}) => {
    const { t } = useLanguage();
    const [games, setGames] = useState<GameListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [gameIdInput, setGameIdInput] = useState('');

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const response = await api.getGames();
                if (response.success) {
                    setGames(response.data);
                } else {
                    throw new Error(response.error || 'ゲーム取得に失敗しました');
                }
            } catch (error) {
                console.error('ゲーム取得エラー:', error);
                showModal('ゲーム一覧の取得に失敗しました。');
            } finally {
                setLoading(false);
            }
        };

        fetchGames();

        // 定期的にゲーム一覧を更新
        const interval = setInterval(fetchGames, 3000);
        return () => clearInterval(interval);
    }, [showModal]);

    const createNewGame = async () => {
        setLoading(true);
        try {
            const response = await api.createGame(sessionId, playerName);

            if (response.success) {
                const gameUrl = `${window.location.origin}?gameId=${response.data.id}`;
                showModal(
                    `新しいゲームを作成しました！\n\nこのURLを他のプレイヤーに共有して招待できます:\n${gameUrl}`,
                    true,
                    gameUrl
                );
                onNavigateToGame(response.data.id);
            } else {
                throw new Error(response.error || 'ゲーム作成に失敗しました');
            }
        } catch (error) {
            console.error('ゲーム作成エラー:', error);
            showModal('新しいゲームの作成に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const joinGame = async (gameId: string) => {
        setLoading(true);
        try {
            const response = await api.joinGame(gameId, sessionId);

            if (response.success) {
                onNavigateToGame(gameId);
            } else {
                throw new Error(response.error || 'ゲーム参加に失敗しました');
            }
        } catch (error) {
            console.error('ゲーム参加エラー:', error);
            showModal('ゲームへの参加に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const joinGameById = async () => {
        if (!gameIdInput.trim()) {
            showModal('ゲームIDを入力してください。');
            return;
        }

        await joinGame(gameIdInput.trim());
        setGameIdInput('');
    };

    const deleteGame = async (gameId: string) => {
        if (!confirm('本当にこのゲームを削除しますか？')) {
            return;
        }

        setLoading(true);
        try {
            const response = await api.deleteGame(gameId, sessionId);

            if (response.success) {
                showModal('ゲームを削除しました。');
                // ゲーム一覧を更新
                const gamesResponse = await api.getGames();
                if (gamesResponse.success) {
                    setGames(gamesResponse.data);
                }
            } else {
                throw new Error(response.error || 'ゲーム削除に失敗しました');
            }
        } catch (error) {
            console.error('ゲーム削除エラー:', error);
            showModal('ゲームの削除に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const getGameStatusText = (status: string) => {
        switch (status) {
            case 'waiting':
                return t('lobby.statusWaiting');
            case 'playing':
                return t('lobby.statusPlaying');
            case 'finished':
                return t('lobby.statusFinished');
            default:
                return status;
        }
    };

    return (
        <div className="relative min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="absolute top-4 right-4">
                <LanguageSelector />
            </div>
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg w-full max-w-2xl text-center">
                <h2 className="text-3xl font-bold mb-6 text-slate-900">{t('lobby.title')}</h2>
                <p className="text-slate-700 mb-4">
                    {t('lobby.playerName')}: <span className="font-mono text-sm">{playerName}</span>
                </p>
                <button
                    onClick={createNewGame}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-200 transform hover:scale-105 active:scale-95 mb-6 disabled:opacity-50"
                >
                    {loading ? t('lobby.creating') : t('lobby.createGame')}
                </button>

                <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-lg font-semibold mb-3 text-slate-800">{t('lobby.joinByGameId')}</h4>
                    <div className="flex space-x-2">
                        <input
                            type="text"
                            value={gameIdInput}
                            onChange={(e) => setGameIdInput(e.target.value)}
                            placeholder={t('lobby.gameIdPlaceholder')}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={joinGameById}
                            disabled={loading || !gameIdInput.trim()}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {t('lobby.join')}
                        </button>
                    </div>
                </div>

                <h3 className="text-2xl font-bold mb-4 text-slate-800">{t('lobby.gameList')}</h3>
                {loading && <p className="text-slate-600">{t('common.loading')}</p>}
                {!loading && games.length === 0 && (
                    <p className="text-slate-600">{t('lobby.noGamesAvailable')}</p>
                )}
                {!loading && games.length > 0 && (
                    <ul className="space-y-3">
                        {games.map(game => (
                            <li key={game.id} className="bg-slate-100 p-4 rounded-lg flex flex-col sm:flex-row justify-between items-center shadow-sm">
                                <div className="text-left mb-2 sm:mb-0 flex-1">
                                    <p className="font-semibold text-lg">
                                        {t('lobby.creator')}: <span className="text-sm">{game.creator_name}</span>
                                    </p>
                                    <p className="text-sm text-slate-600 mb-1">
                                        {t('game.gameId')}: <span
                                            className="font-mono bg-slate-200 px-2 py-1 rounded cursor-pointer hover:bg-slate-300 transition-colors"
                                            onClick={() => {
                                                navigator.clipboard.writeText(game.id);
                                                showModal(t('common.copied'));
                                            }}
                                            title={t('common.clickToCopy')}
                                        >
                                            {game.id.substring(0, 8)}...
                                        </span>
                                    </p>
                                    <p className="text-sm text-slate-600">
                                        {t('lobby.playerCount')}: {game.player_count}/2
                                    </p>
                                    <p className={`text-sm font-medium ${game.status === 'waiting' ? 'text-orange-500' :
                                        game.status === 'playing' ? 'text-blue-500' : 'text-gray-500'
                                        }`}>
                                        {t('lobby.status')}: {getGameStatusText(game.status)}
                                    </p>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => joinGame(game.id)}
                                        disabled={loading || game.player_count >= 2}
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                        {t('lobby.join')}
                                    </button>
                                    {game.creator_id === sessionId && (
                                        <button
                                            onClick={() => deleteGame(game.id)}
                                            disabled={loading}
                                            className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                                        >
                                            {t('lobby.delete')}
                                        </button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}; 